import { z } from 'zod';
import { permissionSetSchema } from './permissions';
import { colourSchema } from './classification';
import { patchOf } from './zod-setup';
export const createOrgSchema = z
    .object({
    name: z.string().trim().min(1, 'Give the business a name').max(160),
    /** Optional; assigned by rotation among the person's books when absent. */
    colour: colourSchema.optional(),
    /**
     * URL-safe handle. Derived from the name when absent, and immutable once
     * set - it appears in customer-portal links, and changing it would break
     * every link already emailed to a customer.
     */
    slug: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens')
        .min(2)
        .max(60)
        .optional(),
    /** The organisation the book lands in. Absent: the one the person owns (or their active book's). */
    organisationId: z.string().trim().max(36).optional(),
})
    .strict();
/**
 * Organisations: the shelf a book stands on. Owned by one person, who names
 * it, creates books in it and moves books between the ones they own.
 */
export const createOrganisationSchema = z
    .object({
    name: z.string().trim().min(1, 'Give the organisation a name').max(120),
    colour: colourSchema.optional(),
})
    .strict();
export const updateOrganisationSchema = z
    .object({
    name: z.string().trim().min(1, 'Give the organisation a name').max(120).optional(),
    colour: colourSchema.nullable().optional(),
    /** Hand it to another person - one who already belongs to a book in it. */
    ownerId: z.string().trim().max(36).optional(),
})
    .strict();
export const moveBookSchema = z.object({ organisationId: z.string().trim().min(1).max(36) }).strict();
/** The same invitation, made in every book of the organisation the inviter may invite into. */
export const inviteToOrganisationSchema = z
    .object({
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    roleKey: z.enum(['admin', 'member', 'auditor']),
})
    .strict();
export const updateOrgSchema = z
    .object({
    name: z.string().trim().min(1).max(160).optional(),
    // Every letterhead field can be CLEARED: the settings sheet sends null for
    // a blanked field, and a letterhead line that can be set but never taken
    // back again is one somebody types a space into.
    legalName: z.string().trim().max(200).nullable().optional(),
    email: z.string().trim().email().nullable().optional(),
    phone: z.string().trim().max(40).nullable().optional(),
    website: z.string().trim().url().max(200).nullable().optional(),
    addressLine1: z.string().trim().max(200).nullable().optional(),
    addressLine2: z.string().trim().max(200).nullable().optional(),
    city: z.string().trim().max(100).nullable().optional(),
    state: z.string().trim().max(100).nullable().optional(),
    postalCode: z.string().trim().max(20).nullable().optional(),
    country: z.string().trim().max(100).nullable().optional(),
    logoFileId: z.string().trim().max(36).nullable().optional(),
    /** The book's colour, from the category palette. Migration 0016. */
    colour: colourSchema.optional(),
})
    .strict();
export const inviteMemberSchema = z
    .object({
    email: z.string().trim().toLowerCase().email(),
    roleId: z.string().min(1, 'Pick a role'),
})
    .strict();
export const updateMembershipSchema = z
    .object({
    roleId: z.string().min(1).optional(),
    status: z.enum(['active', 'disabled']).optional(),
})
    .strict();
export const createRoleSchema = z
    .object({
    name: z.string().trim().min(1).max(60),
    description: z.string().trim().max(300).optional(),
    permissions: permissionSetSchema,
})
    .strict();
export const updateRoleSchema = patchOf(createRoleSchema).strict();
// ── API keys ──────────────────────────────────────────────────────────────
/**
 * A key is named, and nothing else.
 *
 * No scopes and no expiry yet, deliberately: a key acts as the person who made
 * it, with their role, which is already a real boundary. Adding scopes means a
 * column, a UI to choose them, and an enforcement point - and half-built scopes
 * that are shown but not enforced are worse than none.
 */
export const createApiKeySchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(1, 'Name the key after where it will be used')
        .max(120),
})
    .strict();
//# sourceMappingURL=orgs.js.map